
import * as d3 from 'd3';
import moment, { Moment } from 'moment';
import { clearTimeout, setTimeout } from 'timers';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { AnswerType, IAnswerDatum, Point } from '../../shared/contracts/AnswerContract';
import { BarometerTab } from './BarometerModel';

/**
 * In d3-land, we often refer to data as 'data' with short variable names.
 * It makes the various short function calls more readable and is easier on memory for large data
 */
interface D {
    snapshotId: number;
    d: Date;
    v: Point;
}

@Component
export default class AnswerTrend extends Vue {
    @Prop() history: IAnswerDatum[];

    @Prop() selectedSnapshotId: number;

    @Prop() selectedTab: BarometerTab;

    @Prop() answerType: AnswerType;
    @Watch('selectedTab', { deep: true })
    onSelectedTabChanged(val: BarometerTab) {
        this.updateTrend();
    }

    private today: Date = new Date();
    private width: number = 0;
    private height: number = 0;
    private svgElement: any = undefined;
    private margin = { top: 20, right: 30, bottom: 20, left: 60 };

    /**
     * The full AnswerTrend data points range for creating the fluent line.
     *
     */
    get fullTrend(): D[] {
        if (!this.history) {
            return [];
        }
        const result = this.history
            .map(h => ({
                d: this.toDate(h.d),
                v: h.v,
                snapshotId: h.snapshotId
            }))
            .sort((h1, h2) => h1.d.getTime() - h2.d.getTime());

        return result;
    }
    /**
     * The AnswerTrend data points range from the snapshot date (lastPoint) up until ~ a year before.
     *
     */
    get yearTrend(): D[] {
        return this.fullTrend.filter(h => moment(h.d).isSameOrAfter(moment(this.yearAgo).subtract(1, 'day')));
    }

    get yearAgo(): Date {
        return moment(this.today)
            .subtract(1, 'year')
            .toDate();
    }

    mounted() {
        // Wait untill all elements in the carousel are rendered before drawing graph
        setTimeout(() => {
            this.svgElement = d3.select(this.$el).select('svg');
            this.updateTrend();
            this.addResizeEventListener();
        }, 0);
    }

    private addResizeEventListener() {
        let timeoutId: any;
        window.addEventListener('resize', () => {
            if (!timeoutId) {
                timeoutId = setTimeout(() => {
                    // Throttle resize
                    this.updateTrend();
                    clearTimeout(timeoutId);
                    timeoutId = undefined;
                }, 100);
            }
        });
    }

    private toDate(s: string): Date {
        return moment(s).toDate();
    }

    private updateTrend() {
        this.height = 170 - this.margin.top - this.margin.bottom; // Fixed height
        if (this.svgElement) {
            const svgClientRect = (this.svgElement.node() as any).getBoundingClientRect();
            const newWidth = svgClientRect.width - this.margin.left - this.margin.right; // TODO get width from element width and recalculate on resize/tab change
            if (newWidth > 0 && this.width !== newWidth) {
                this.width = newWidth;
                this.refreshTrend();
            }
        }
    }

    private refreshTrend() {
        this.svgElement.selectAll('*').remove();

        const pointColors = { Positive: '--green', Neutral: '--yellow', Negative: '--red' };
        const sixMoAgo = (moment() as Moment).subtract(6, 'months').toDate();

        const svg = this.svgElement
            .append('g')
            .attr('transform', 'translate(' + this.margin.left + ',' + this.margin.top + ')');

        const x = d3
            .scaleTime()
            .range([0, this.width] as ReadonlyArray<number>)
            .domain([
                moment(this.yearAgo)
                    .subtract(7, 'days')
                    .toDate(),
                this.today
            ]);
        // Take a few days extra for the scale

        const y = d3
            .scaleBand()
            .domain([Point.Positive, Point.Neutral, Point.Negative])
            .range([0, this.height])
            .padding(0.1);

        const xAxis = d3
            .axisBottom(x)
            .tickSize(0)
            .scale(x)
            .tickValues([this.yearAgo, sixMoAgo, this.today])
            .tickFormat((d: any, i) => {
                switch (i) {
                    case 0:
                        return ' 1 year ago';
                    case 1:
                        return '6 months ago';
                    case 2:
                        return 'Today';
                }
            });

        const yAxis = d3
            .axisLeft(y)
            .tickSize(0)
            .scale(y)
            .tickValues([Point.Positive, Point.Neutral, Point.Negative])
            .tickFormat((d: any, i) => {
                switch (this.answerType) {
                    case 'Rotation':
                        switch (i) {
                            case 0:
                                return 'Not yet';
                            case 1:
                                return 'Maybe';
                            case 2:
                                return 'Yes';
                        }
                    default:
                        switch (i) {
                            case 0:
                                return 'Great!';
                            case 1:
                                return 'So so';
                            case 2:
                                return 'Not at all';
                        }
                }
            });

        // Position along the middle of the great/so-so/not bars
        const yPosition = (d: Point) => {
            switch (d) {
                case Point.Positive:
                    return y.bandwidth() / 2 + y.padding() * y.bandwidth();
                case Point.Neutral:
                    return y.bandwidth() * 1.5 + y.padding() * y.bandwidth() * 2;
                case Point.Negative:
                    return y.bandwidth() * 2.5 + y.padding() * y.bandwidth() * 3;
            }
        };

        const createPath = (d3 as any)
            .line()
            .x((d: D) => x(d.d))
            .y((d: D) => yPosition(d.v))
            .curve(d3.curveMonotoneX);

        svg.append('path')
            .attr('clip-path', 'url(#trend-clip)')
            .attr('class', 'answer-trend-line')
            .attr('d', createPath(this.fullTrend));

        // Only show trend line from y-axis
        svg.append('clipPath')
            .attr('id', 'trend-clip')
            .append('rect')
            .attr('x', 0)
            .attr('y', 0)
            .attr('width', this.width + this.margin.right / 2)
            .attr('height', this.height);

        svg.append('g')
            .attr('class', 'x-axis')
            .attr('transform', 'translate(0,' + this.height + ')')
            .style('font-size', '12px')
            .style('font-family', 'Lato, "Helvetica Neue", Helvetica, Arial, sans-serif')
            .call(xAxis as any)
            .selectAll('text')
            .attr('transform', 'translate(0, 5)'); // tick text

        svg.append('g')
            .attr('class', 'y-axis')
            .style('font-size', '12px')
            .style('font-family', 'Lato, "Helvetica Neue", Helvetica, Arial, sans-serif')
            .call(yAxis)
            .selectAll('text')
            .attr('transform', 'translate(-5, 0)'); // tick text

        // Background 'target' bar
        svg.selectAll('rect')
            .data([{ c: 'bar-green', v: 'Great!' }])
            .enter()
            .append('rect')
            .attr('x', 0)
            .attr('y', (d: any) => y(d.v))
            .attr('width', this.width + this.margin.right / 2)
            .attr('height', y.bandwidth())
            .attr('class', (d: any) => d.c);

        const tooltip = svg
            .append('g')
            .attr('class', 'answer-trend-tooltip')
            .style('display', 'none');
        tooltip
            .append('text')
            .style('font-size', '12px')
            .style('font-family', 'Lato, "Helvetica Neue", Helvetica, Arial, sans-serif');

        svg.selectAll('circle')
            .data(this.yearTrend)
            .enter()
            .append('circle')
            .attr('r', (d: D) => 5)
            .attr('cx', (d: D) => x(d.d))
            .attr('cy', (d: D) => yPosition(d.v))
            .attr(
                'class',
                (d: D) =>
                    `answer-trend-point answer-trend-point${pointColors[d.v]}${
                        d.snapshotId === this.selectedSnapshotId ? '--selected' : ''
                    }`
            )
            .on('mouseover', (d: D) => {
                tooltip.select('text').text(() => moment(d.d).format("MMM Do 'YY"));
                tooltip
                    .style('display', null)
                    .attr(
                        'transform',
                        `translate(${x(d.d) - tooltip.node().getBoundingClientRect().width / 2},${y(d.v) + 8})`
                    );
            })
            .on('mouseout', (d: D) => {
                tooltip.style('display', 'none');
            })
            .on('click', (d: D) => this.$emit('selectSnapshotId', d.snapshotId));
    }
}
